"use client";

import { useState, useEffect, useRef } from "react";
import {
  Camera,
  ImageIcon,
  Hash,
  Check,
  X,
  Search,
  ChevronDown,
} from "lucide-react";
import Button from "./Button";
import Popover from "./Popover";
import { classNames } from "../../utils/classNames";
import ImagePickerModal from "./ImagePickerModal";
import { successToast, errorToast, warningToast } from '../../utils/toasters'
import { getScoops, getHoops } from "../../services/scoops";
import {
  base64ToFiles,
  filesToBase64,
  isAuthenticated,
} from "../../utils/helpers";
import { useNavigate } from "react-router-dom";
import { handleAddGroupPost } from "../../services/group";

function Hashtag({ tag, onClick, selected }) {
  return (
    <button
      onClick={onClick}
      className={classNames(
        "px-3 py-1 rounded-full text-sm font-medium",
        selected
          ? "bg-black text-white"
          : "border text-black hover:bg-gray-800 hover:text-white"
      )}
    >
      {tag}
    </button>
  );
}

function CreateGroupPostForm({
  id,
  followingUsers = [],
  postings = [],
  onPostSuccess = () => {}, // Simplified prop name
  groupTopic = "General",
  groupTopicId = "1",
}) {
  const [content, setContent] = useState("");
  const [selectedHashtags, setSelectedHashtags] = useState([]);
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [imagePickerMode, setImagePickerMode] = useState("upload");
  const [isHashtagPopoverOpen, setIsHashtagPopoverOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [geohash, setGeohash] = useState("9v6m");
  const navigate = useNavigate();

  // Autocomplete/mention feature states
  const textareaRef = useRef(null);
  const mentionGuideRef = useRef(null);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [mentionType, setMentionType] = useState(null);
  const [mentionText, setMentionText] = useState("");
  const [mentionOptions, setMentionOptions] = useState([]);
  const [showMentionGuide, setShowMentionGuide] = useState(false);
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);

  // State for API data
  const [scoops, setScoops] = useState([]);
  const [hoops, setHoops] = useState([]);
  const [availableHoops, setAvailableHoops] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // State for the Galli Talks category
  const [galliTalksCategory, setGalliTalksCategory] = useState({
    id: "1",
    name: "Galli Talks",
    iconPath: "/scoops/galli_talks.png",
  });

  // Fetch scoops and hoops when component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        const [scoopsData, hoopsData] = await Promise.all([
          getScoops(),
          getHoops(),
        ]);

        setScoops(scoopsData);
        setHoops(hoopsData);

        const matchingScoop = scoopsData.find(
          (scoop) => scoop.scoopname.toLowerCase() === "galli talks"
        );

        if (matchingScoop) {
          setGalliTalksCategory({
            id: matchingScoop.scoopid.toString(),
            name: "Galli Talks",
            iconPath: "/scoops/galli_talks.png",
          });

          if (matchingScoop.hoops) {
            const scoopHoops = matchingScoop.hoops.map((h) => h.hoopid);
            const filteredHoops = hoopsData.filter((h) =>
              scoopHoops.includes(h.hoopid)
            );
            const groupedFilteredHoops = groupHoopsByCategory(filteredHoops);
            setAvailableHoops(groupedFilteredHoops);
          }
        } else {
          const groupedHoops = groupHoopsByCategory(hoopsData);
          setAvailableHoops(groupedHoops);
        }
      } catch (error) {
        errorToast(
          "Failed to load categories and hashtags: " + error.message,
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
          }
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Group hoops into arrays for display
  const groupHoopsByCategory = (hoopsData) => {
    const groups = [];
    for (let i = 0; i < hoopsData.length; i += 6) {
      groups.push(hoopsData.slice(i, i + 6));
    }
    return groups;
  };

  // Get geolocation when component mounts
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGeohash("9v6m");
        },
        (error) => {
          console.error("Error getting geolocation:", error);
        }
      );
    }
  }, []);

  // Handle text input and detect mentions
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);

    const cursorPos = e.target.selectionStart;
    setCursorPosition(cursorPos);

    const textBeforeCursor = newContent.substring(0, cursorPos);
    const words = textBeforeCursor.split(/\s/);
    const currentWord = words[words.length - 1];

    if (currentWord.startsWith("#")) {
      const searchText = currentWord.substring(1).toLowerCase();
      setMentionType("#");
      setMentionText(searchText);
      setSelectedMentionIndex(0);

      const filteredHoops = hoops
        .filter((hoop) => {
          const galliTalksScoop = scoops.find(
            (s) => s.scoopname.toLowerCase() === "galli talks"
          );

          if (!galliTalksScoop || !galliTalksScoop.hoops) return false;

          const scoopHoops = galliTalksScoop.hoops.map((h) => h.hoopid);
          return (
            scoopHoops.includes(hoop.hoopid) &&
            hoop.hooptag.toLowerCase().includes(searchText)
          );
        })
        .slice(0, 5);

      setMentionOptions(
        filteredHoops.map((hoop) => ({
          id: hoop.hoopid.toString(),
          name: hoop.hooptag,
          type: "hoop",
        }))
      );

      setShowMentionGuide(true);
      calculateMentionPosition();
    } else {
      setShowMentionGuide(false);
    }
  };

  // Calculate position for mention guide
  const calculateMentionPosition = () => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const textareaRect = textarea.getBoundingClientRect();
    const containerEl = textarea.parentElement;
    const containerRect = containerEl.getBoundingClientRect();
    const cursorPosition = textarea.selectionEnd;

    const tempElement = document.createElement("div");
    tempElement.style.position = "absolute";
    tempElement.style.visibility = "hidden";
    tempElement.style.whiteSpace = "pre-wrap";
    tempElement.style.width = `${textareaRect.width}px`;

    const textareaStyles = window.getComputedStyle(textarea);
    const stylesToCopy = [
      "font",
      "fontSize",
      "fontFamily",
      "lineHeight",
      "letterSpacing",
      "textTransform",
      "padding",
      "paddingTop",
      "paddingRight",
      "paddingBottom",
      "paddingLeft",
      "boxSizing",
      "borderWidth",
    ];

    stylesToCopy.forEach((style) => {
      tempElement.style[style] = textareaStyles[style];
    });

    const textBeforeCursor = textarea.value.substring(0, cursorPosition);
    tempElement.innerHTML =
      textBeforeCursor.replace(/\n/g, "<br>") + '<span id="cursor"></span>';

    document.body.appendChild(tempElement);
    tempElement.style.top = `${textareaRect.top}px`;
    tempElement.style.left = `${textareaRect.left}px`;

    const cursorSpan = document.getElementById("cursor");

    if (!cursorSpan) {
      document.body.removeChild(tempElement);
      return;
    }

    const cursorRect = cursorSpan.getBoundingClientRect();
    document.body.removeChild(tempElement);

    let top = cursorRect.top - textareaRect.top + textarea.scrollTop;
    let left = cursorRect.left - textareaRect.left + textarea.scrollLeft;

    const lineHeight =
      Number.parseFloat(textareaStyles.lineHeight) ||
      Number.parseFloat(textareaStyles.fontSize) * 1.2;
    top += lineHeight;

    const paddingTop = Number.parseFloat(textareaStyles.paddingTop) || 0;
    const paddingLeft = Number.parseFloat(textareaStyles.paddingLeft) || 0;
    top += paddingTop;
    left += paddingLeft;

    const dropdownWidth = 256;
    const dropdownHeight = 208;

    const rightEdge = left + dropdownWidth;
    const containerWidth = containerRect.width;
    if (rightEdge > containerWidth - 20) {
      left = Math.max(10, containerWidth - dropdownWidth - 20);
    }

    const bottomEdge = top + dropdownHeight;
    const containerHeight = containerRect.height;
    if (bottomEdge > containerHeight - 20) {
      top = Math.max(10, top - dropdownHeight - lineHeight);
    }

    const scaleFactor = 1 / (window.visualViewport?.scale || 1);
    if (scaleFactor !== 1) {
      top = top * scaleFactor;
      left = left * scaleFactor;
    }

    setMentionPosition({
      top,
      left,
      containerWidth: containerWidth,
      textareaWidth: textareaRect.width,
    });
  };

  // Handle selection from mention guide
  const handleMentionSelect = (option) => {
    const textBeforeCursor = content.substring(0, cursorPosition);
    const textAfterCursor = content.substring(cursorPosition);

    const words = textBeforeCursor.split(/\s/);
    const currentWordIndex = words.length - 1;
    const currentWord = words[currentWordIndex];

    words[currentWordIndex] = "";

    const newTextBefore = words.join(" ").replace(/\s+/g, " ").trim();
    const newText =
      newTextBefore + (newTextBefore ? " " : "") + textAfterCursor.trimStart();

    setContent(newText);
    setShowMentionGuide(false);

    if (option.type === "hoop") {
      const hoop = hoops.find((h) => h.hoopid.toString() === option.id);
      if (hoop && !selectedHashtags.some((h) => h.hoopid === hoop.hoopid)) {
        setSelectedHashtags([...selectedHashtags, hoop]);
      }
    }

    if (textareaRef.current) {
      textareaRef.current.focus();
      const newCursorPosition = newTextBefore.length;
      setTimeout(() => {
        textareaRef.current.selectionStart = newCursorPosition;
        textareaRef.current.selectionEnd = newCursorPosition;
      }, 0);
    }
  };

  const toggleHashtag = (hoop) => {
    if (selectedHashtags.some((h) => h.hoopid === hoop.hoopid)) {
      setSelectedHashtags(
        selectedHashtags.filter((h) => h.hoopid !== hoop.hoopid)
      );
    } else {
      setSelectedHashtags([...selectedHashtags, hoop]);
    }
  };

  const openImagePicker = (mode) => {
    setImagePickerMode(mode);
    setIsImagePickerOpen(true);
  };

  const handleImageSelected = (imageData) => {
    if (Array.isArray(imageData)) {
      const newImages = [...images];
      for (const img of imageData) {
        if (newImages.length < 2) {
          newImages.push(img);
        } else {
          warningToast("Maximum 2 images allowed", {
            position: "top-right",
            autoClose: 3000,
          });
          break;
        }
      }
      setImages(newImages);
    } else if (imageData) {
      if (images.length < 2) {
        setImages([...images, imageData]);
      } else {
        warningToast("Maximum 2 images allowed", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    }
  };

  const removeImage = (index) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    setImages(newImages);
  };

  // Load saved draft data if present
  useEffect(() => {
    const loadSavedData = async () => {
      try {
        setIsLoading(true);

        const savedContent = localStorage.getItem("content");
        if (savedContent) {
          setContent(savedContent);
          localStorage.removeItem("content");
        }

        const savedHashtags = localStorage.getItem("selectedHashtags");
        if (savedHashtags) {
          const parsedHashtags = JSON.parse(savedHashtags);
          setSelectedHashtags(parsedHashtags);
          localStorage.removeItem("selectedHashtags");
        }

        const savedImages = localStorage.getItem("images");
        if (savedImages) {
          const imageFiles = await base64ToFiles(savedImages);
          setImages(imageFiles);
          localStorage.removeItem("images");
        }
      } catch (error) {
        console.error("Error loading saved data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSavedData();
  }, []);

  // 🎯 MAIN CHANGE: Simplified handlePost function
  const handlePost = async () => {
    try {
      if (!isAuthenticated()) {
        localStorage.setItem("target", window.location.pathname);
        localStorage.setItem("content", content);
        localStorage.setItem(
          "selectedHashtags",
          JSON.stringify(selectedHashtags)
        );
        localStorage.setItem("images", await filesToBase64(images));
        navigate("/signin");
        return;
      }

      setIsProcessing(true);

      if (!content.trim() && images.length === 0) {
        errorToast("Please enter some content or add an image", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
        });
        return;
      }

      // Create FormData to send files
      const formData = new FormData();
      formData.append("content", content);
      formData.append("scoopId", galliTalksCategory.id);
      formData.append("geoHash", geohash);
      formData.append(
        "hoopIds",
        selectedHashtags.map((h) => h.hoopid).join(",")
      );

      // Add image files
      images.forEach((imageFile, index) => {
        if (index < 2) {
          formData.append("photopath", imageFile);
        }
      });

      console.log(
        "Selected hashtags:",
        selectedHashtags.map((h) => h.hooptag).join(", ")
      );

      // Send data to server
      const response = await handleAddGroupPost(formData, id);

      // Show success message
      successToast("Post has been published successfully", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });

      // 🚀 KEY CHANGE: Call onPostSuccess to fetch fresh data from server
      await onPostSuccess();

      // Reset form
      setContent("");
      setSelectedHashtags([]);
      setImages([]);
    } catch (error) {
      console.error("Error posting:", error);
      errorToast(
        "" + (error.response?.data?.message ?? error.data?.message ?? error),
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
        }
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDoneHashtags = () => {
    setIsHashtagPopoverOpen(false);
  };

  // MentionItem component
  const MentionItem = ({ option, onClick, isSelected }) => {
    const icon = <Hash size={14} />;
    const bgColor = "bg-fuchsia-100";

    const highlightedName = () => {
      if (!mentionText) return option.name;

      const index = option.name
        .toLowerCase()
        .indexOf(mentionText.toLowerCase());
      if (index === -1) return option.name;

      const before = option.name.substring(0, index);
      const match = option.name.substring(index, index + mentionText.length);
      const after = option.name.substring(index + mentionText.length);

      return (
        <>
          {before}
          <span className="font-bold bg-yellow-100">{match}</span>
          {after}
        </>
      );
    };

    return (
      <div
        className={`flex items-center gap-2 px-3 py-2 cursor-pointer border-b border-gray-200 ${
          isSelected ? "bg-gray-100" : "hover:bg-gray-100"
        }`}
        onClick={() => onClick(option)}
      >
        <div
          className={`w-6 h-6 rounded-full flex items-center justify-center ${bgColor}`}
        >
          {icon}
        </div>
        <div className="font-medium">{highlightedName()}</div>
      </div>
    );
  };

  const hashtagPopoverContent = (
    <div className="p-4 flex flex-col gap-4 max-h-[400px] overflow-y-auto">
      {isLoading ? (
        <div className="text-center">Loading hashtags...</div>
      ) : availableHoops.length === 0 ? (
        <div className="text-center">Loading hashtags...</div>
      ) : (
        <>
          {availableHoops.map((group, index) => (
            <div key={index} className="flex flex-wrap gap-2">
              {group.map((hoop) => (
                <Hashtag
                  key={hoop.hoopid}
                  tag={hoop.hooptag}
                  selected={selectedHashtags.some(
                    (h) => h.hoopid === hoop.hoopid
                  )}
                  onClick={() => toggleHashtag(hoop)}
                />
              ))}
            </div>
          ))}
          <Button
            className="ml-auto flex items-center gap-1 text-black text-sm rounded-lg bg-[#7B189F] hover:bg-[#8e23b5] hover:text-white"
            onClick={handleDoneHashtags}
          >
            <Check size={16} />
            <span>Done</span>
          </Button>
        </>
      )}
    </div>
  );

  // Handle keyboard navigation in mention guide
  const handleKeyDown = (e) => {
    if (showMentionGuide && mentionOptions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedMentionIndex((prevIndex) =>
          prevIndex < mentionOptions.length - 1 ? prevIndex + 1 : prevIndex
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedMentionIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : 0
        );
      } else if (e.key === "Enter" && mentionOptions.length > 0) {
        e.preventDefault();
        handleMentionSelect(mentionOptions[selectedMentionIndex]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setShowMentionGuide(false);
      }
    }
  };

  // Effects for mention guide positioning
  useEffect(() => {
    if (showMentionGuide) {
      calculateMentionPosition();
    }
  }, [content, showMentionGuide]);

  useEffect(() => {
    const handleResize = () => {
      if (showMentionGuide) {
        calculateMentionPosition();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [showMentionGuide]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const handleScroll = () => {
      if (showMentionGuide) {
        calculateMentionPosition();
      }
    };

    textarea.addEventListener("scroll", handleScroll);
    return () => textarea.removeEventListener("scroll", handleScroll);
  }, [showMentionGuide, textareaRef.current]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mentionGuideRef.current &&
        !mentionGuideRef.current.contains(event.target) &&
        textareaRef.current &&
        !textareaRef.current.contains(event.target)
      ) {
        setShowMentionGuide(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <div className="bg-white rounded-lg border p-4 mb-4">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="rounded-full flex items-center gap-2 bg-amber-100 text-gray-700 px-3 py-1.5 cursor-default">
            <div className="w-6 h-6 rounded-full bg-amber-300 text-amber-800 flex items-center justify-center">
              <img
                src="/images/scoops_icon.svg"
                alt="scoops icon"
                width={16}
                height={16}
                className="object-contain"
              />
            </div>
            <span className="font-medium">Galli Talks</span>
          </div>

          <Popover
            content={hashtagPopoverContent}
            align="start"
            open={isHashtagPopoverOpen}
            onOpenChange={setIsHashtagPopoverOpen}
          >
            <Button
              variant="outline"
              className="rounded-full flex items-center gap-2 text-gray-700 bg-fuchsia-100"
            >
              <div className="w-6 h-6 rounded-full bg-fuchsia-300 text-fuchsia-800 flex items-center justify-center">
                <Hash size={14} />
              </div>
              <span>Add Hoop</span>
              <ChevronDown size={16} />
            </Button>
          </Popover>
        </div>

        <div className="mb-4 relative">
          <textarea
            ref={textareaRef}
            className="w-full p-2 min-h-[100px] border-none outline-none resize-none"
            placeholder="What's on your mind? Pick # Hoop — your voice, your vibe"
            value={content}
            onChange={handleContentChange}
            onKeyDown={handleKeyDown}
          />

          {showMentionGuide && (
            <div
              ref={mentionGuideRef}
              className="bg-white border rounded-md shadow-lg overflow-y-auto max-h-52 w-64"
              style={{
                position: "relative",
                zIndex: 99999,
                maxWidth: "calc(100% - 1rem)",
                marginTop: "-0.75rem",
                marginLeft: mentionPosition.left,
                display: showMentionGuide ? "block" : "none",
              }}
            >
              <div className="sticky top-0 p-2 bg-gray-100 border-b flex items-center gap-2">
                <Search size={14} className="text-gray-600" />
                <span className="text-sm font-medium">
                  🏷️ Select a hoop{mentionText ? `: ${mentionText}` : ""}
                </span>
              </div>

              {mentionOptions.length > 0 ? (
                <div className="py-1">
                  {mentionOptions.map((option, index) => (
                    <MentionItem
                      key={`${option.type}-${option.id}`}
                      option={option}
                      onClick={handleMentionSelect}
                      isSelected={index === selectedMentionIndex}
                    />
                  ))}
                </div>
              ) : (
                <div className="p-3 text-center text-gray-500 italic">
                  No matches found
                  <div className="text-xl mt-1">🔍</div>
                </div>
              )}

              <div className="p-2 bg-gray-50 border-t text-xs text-gray-500 flex justify-between">
                <span>↑↓ to navigate</span>
                <span>↵ to select</span>
                <span>Esc to close</span>
              </div>
            </div>
          )}
        </div>

        {/* Display multiple images */}
        {images.length > 0 && (
          <div className="mb-4 grid grid-cols-2 gap-2">
            {images.map((img, index) => (
              <div key={index} className="relative">
                <img
                  src={img instanceof File ? URL.createObjectURL(img) : img}
                  alt={`Uploaded content ${index + 1}`}
                  className="w-full h-auto rounded-md object-cover aspect-square"
                />
                <button
                  className="absolute top-2 right-2 bg-white/80 rounded-full p-1 hover:bg-white"
                  onClick={() => removeImage(index)}
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {selectedHashtags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-black text-white flex items-center gap-1">
              Galli Talks
            </span>

            {selectedHashtags.map((hoop) => (
              <span
                key={hoop.hoopid}
                className="px-3 py-1 rounded-full text-xs font-medium bg-fuchsia-100 text-gray-700 flex items-center gap-1"
                onClick={() => toggleHashtag(hoop)}
              >
                {hoop.hooptag}
                <X size={12} className="cursor-pointer" />
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => openImagePicker("camera")}
              className="text-gray-500 hover:text-gray-800 transition-colors"
            >
              <Camera size={20} />
            </button>
            <button
              onClick={() => openImagePicker("upload")}
              className="text-gray-500 hover:text-gray-800 transition-colors"
            >
              <ImageIcon size={20} />
            </button>
          </div>

          <Button
            variant="primary"
            className="rounded-full px-6 bg-[#7B189F] text-white hover:bg-[#8823ad] disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handlePost}
            disabled={isProcessing || (!content.trim() && images.length === 0)}
          >
            {isProcessing ? "Posting..." : "Post"}
          </Button>
        </div>

        <ImagePickerModal
          isOpen={isImagePickerOpen}
          onClose={() => setIsImagePickerOpen(false)}
          onImageSelected={handleImageSelected}
          mode={imagePickerMode}
          allowMultiple={true}
        />
      </div>
    </>
  );
}

export default CreateGroupPostForm;
